/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apipost, apiget } from '../../../service/api';

// Quick-reply templates
const QUICK_REPLIES = [
  "Thanks for your inquiry, I'll call you shortly!",
  "Your pre-approval is ready, please check your email.",
  "Just following up on our conversation. Do you have any questions?",
  "I have some great news about your loan application. When is a good time to talk?",
];

const SmsPanel = ({ open, handleClose, leadId, leadData, onSuccess, apiEndpoint, smsHistoryEndpoint, extraPayload }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const messagesEndRef = useRef(null);

  const phoneNumber = leadData?.phoneNumber || '';
  const borrowerName = `${leadData?.firstName || ''} ${leadData?.lastName || ''}`.trim();

  // Apply merge fields
  const applyMergeFields = (text) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return text
      .replace(/\{firstName\}/g, leadData?.firstName || '')
      .replace(/\{lastName\}/g, leadData?.lastName || '')
      .replace(/\{loanOfficerName\}/g, `${user.firstName || ''} ${user.lastName || ''}`.trim());
  };

  // Fetch SMS history
  const initialLoadDone = useRef(false);
  const fetchHistory = async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const result = await apiget(smsHistoryEndpoint || `communication/sms/${leadId}`);
      if (result && result.status === 200) {
        setMessages(result.data.result || []);
      }
    } catch (error) {
      console.error('Failed to fetch SMS history:', error);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  useEffect(() => {
    if (!open || !leadId) return undefined;

    initialLoadDone.current = false;
    fetchHistory();

    // Poll for new messages every 5 seconds while panel is open
    const interval = setInterval(fetchHistory, 5000);
    return () => clearInterval(interval);
  }, [open, leadId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!messageText.trim()) return;
    if (!phoneNumber) {
      toast.error('No phone number available for this lead.');
      return;
    }

    const body = applyMergeFields(messageText.trim());

    try {
      setSending(true);

      // Optimistic append
      const optimisticMsg = {
        _id: `temp-${Date.now()}`,
        direction: 'outbound',
        to: phoneNumber,
        body,
        createdOn: new Date().toISOString(),
        status: 'sending',
      };
      setMessages((prev) => [...prev, optimisticMsg]);
      setMessageText('');

      const result = await apipost(apiEndpoint || 'communication/send-sms', {
        leadId,
        to: phoneNumber,
        body,
        ...(extraPayload || {}),
      });

      if (result && result.status === 200) {
        // Replace optimistic message with real one
        setMessages((prev) =>
          prev.map((m) => (m._id === optimisticMsg._id ? result.data.result : m))
        );
        if (onSuccess) onSuccess(result);
      } else {
        // Remove optimistic message on failure
        setMessages((prev) => prev.filter((m) => m._id !== optimisticMsg._id));
        toast.error('Failed to send SMS.');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
    } finally {
      setSending(false);
    }
  };

  const handleQuickReply = (text) => {
    setMessageText(applyMergeFields(text));
    setShowQuickReplies(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDialogClose = () => {
    setMessageText('');
    setShowQuickReplies(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:message-text-outline" style={{ fontSize: 24, color: '#00AB55' }} />
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                SMS - {borrowerName}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {phoneNumber}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleDialogClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Message list */}
        <Box
          sx={{
            height: 400,
            overflowY: 'auto',
            px: 2,
            py: 2,
            bgcolor: '#f5f5f5',
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                No messages yet. Send the first SMS!
              </Typography>
            </Box>
          ) : (
            messages.map((msg) => {
              const isOutbound = msg.direction === 'outbound';
              return (
                <Box
                  key={msg._id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '75%',
                      bgcolor: isOutbound ? '#1890FF' : 'white',
                      color: isOutbound ? 'white' : 'text.primary',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      position: 'relative',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.body}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5} justifyContent="flex-end">
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.7,
                          fontSize: '0.65rem',
                          color: isOutbound ? 'rgba(255,255,255,0.8)' : 'text.disabled',
                        }}
                      >
                        {dayjs(msg.createdOn).format('h:mm A')}
                      </Typography>
                      {msg.status === 'sending' && (
                        <CircularProgress size={10} sx={{ color: isOutbound ? 'rgba(255,255,255,0.7)' : 'grey.400' }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Quick replies */}
        {showQuickReplies && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.disabled" sx={{ mb: 0.5, display: 'block' }}>
              Quick Replies
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {QUICK_REPLIES.map((reply, idx) => (
                <Chip
                  key={idx}
                  label={reply.length > 40 ? `${reply.substring(0, 40)}...` : reply}
                  size="small"
                  onClick={() => handleQuickReply(reply)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    '&:hover': { bgcolor: '#00AB5518' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Input area */}
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <IconButton
          size="small"
          onClick={() => setShowQuickReplies(!showQuickReplies)}
          sx={{ color: showQuickReplies ? '#00AB55' : 'grey.500' }}
        >
          <Icon icon="mdi:lightning-bolt" />
        </IconButton>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={3}
          sx={{ flex: 1 }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={sending || !messageText.trim()}
        >
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};

export default SmsPanel;
